import { useMemo, useCallback } from "react";

import axiosInstance from "src/utils/axios";
import { authenticatedSwrOptions } from "src/utils/constants";

import useAuthenticatedSWR from "src/auth/hooks/use-authenticated-swr";

import { ModulePermissionEndpoints } from "./modulePermissionEndpoints";


// Module
//--------------------------------

export const useGetModules = () => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Module.GetAll, { ...authenticatedSwrOptions });

    return useMemo(() => ({
        modules: data || [],
        modulesLoading: isLoading,
        modulesError: error,
        modulesValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetModulesFiltered = (filter) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Module.GetAllWithFilter, { ...authenticatedSwrOptions, filter });

    return useMemo(() => ({
        filteredModules: data || [],
        filteredModulesLoading: isLoading,
        filteredModulesError: error,
        filteredModulesValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetModuleById = (moduleId) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Module.GetById(moduleId), { ...authenticatedSwrOptions });

    return useMemo(() => ({
        module: data || {},
        moduleLoading: isLoading,
        moduleError: error,
        moduleValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

// Role Module Permission 
//--------------------------------
export function useGetRoleModulePermissions(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.RoleModulePermission.GetAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        roleModulePermissions: data || [],
        roleModulePermissionsLoading: isLoading,
        roleModulePermissionsError: error,
        roleModulePermissionsValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetRoleModulePermission(id) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(id ? ModulePermissionEndpoints.RoleModulePermission.getById(id) : null, authenticatedSwrOptions);

    return useMemo(() => ({
        roleModulePermission: data || {},
        roleModulePermissionLoading: isLoading,
        roleModulePermissionError: error,
        roleModulePermissionValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetRoleModulePermissionOperations() {
    const CreateRoleModulePermission = async (data) => {
        try {
            const response = await axiosInstance.post(
                ModulePermissionEndpoints.RoleModulePermission.Create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating Role Module Permission:', error);
            throw error;
        }
    };

    const UpdateRoleModulePermission = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                ModulePermissionEndpoints.RoleModulePermission.Update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating Role Module Permission :', error);
            throw error;
        }
    };

    const DeleteRoleModulePermission = async (id) => {

        try {
            const response = await axiosInstance.delete(
                ModulePermissionEndpoints.RoleModulePermission.Delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting Role Module Permission :', error);
            throw error;
        }
    };

    return { CreateRoleModulePermission, UpdateRoleModulePermission, DeleteRoleModulePermission };
}

// Page
//--------------------------------
export function useGetPages(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Page.GetAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        pages: data || [],
        pagesLoading: isLoading,
        pagesError: error,
        pagesValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetPage(id) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Page.GetById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        page: data || {},
        pageLoading: isLoading,
        pageError: error,
        pageValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetPageOperations() {
    const CreatePage = async (data) => {
        try {
            const response = await axiosInstance.post(
                ModulePermissionEndpoints.Page.Create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating Page:', error);
            throw error;
        }
    };

    const UpdatePage = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                ModulePermissionEndpoints.Page.Update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating Page :', error);
            throw error;
        }
    };

    const DeletePage = async (id) => {

        try {
            const response = await axiosInstance.delete(
                ModulePermissionEndpoints.Page.Delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting Page :', error);
            throw error;
        }
    };

    const GetPagesByModuleId = async (moduleId) => {
        const data = {
            ModuleId: moduleId
        };
        try {
            const response = await axiosInstance.post(
                ModulePermissionEndpoints.Page.GetAllWithFilter,
                data
            );

            return response.data;
        } catch (error) {
            console.error('Error getting Page Guideline:', error);
            throw error;
        }
    };


    return { CreatePage, UpdatePage, DeletePage, GetPagesByModuleId };
}

// Page Guideline
//--------------------------------
export function useGetPageGuidelines(tempToken = null) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.PageGuideline.GetAll, { ...authenticatedSwrOptions, tempToken });

    return useMemo(() => ({
        pageGuidelines: data || [],
        pageGuidelinesLoading: isLoading,
        pageGuidelinesError: error,
        pageGuidelinesValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetPageGuideline(id) {

    const { data, isLoading, error, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.PageGuideline.GetById(id), authenticatedSwrOptions);

    return useMemo(() => ({
        pageGuideline: data || {},
        pageGuidelineLoading: isLoading,
        pageGuidelineError: error,
        pageGuidelineValidating: isValidating,
    }), [data, isLoading, error, isValidating]);
}

export function useGetPageGuidelineOperations() {
    const CreatePageGuideline = async (data) => {
        try {
            const response = await axiosInstance.post(
                ModulePermissionEndpoints.PageGuideline.Create,
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error creating Page Guideline:', error);
            throw error;
        }
    };

    const UpdatePageGuideline = async (id, data) => {
        try {
            const response = await axiosInstance.put(
                ModulePermissionEndpoints.PageGuideline.Update(id),
                data
            );
            return response.data;
        } catch (error) {
            console.error('Error updating Page Guideline:', error);
            throw error;
        }
    };

    const DeletePageGuideline = async (id) => {

        try {
            const response = await axiosInstance.delete(
                ModulePermissionEndpoints.PageGuideline.Delete(id)
            );
            return response.data;
        } catch (error) {
            console.error('Error deleting Page Guideline:', error);
            throw error;
        }
    };

    const GetPageGuidelineByPageId = useCallback(async (pageId, sortId = 1) => {
        const data = {
            PageId: pageId,
            SortId: sortId
        };
        try {
            const response = await axiosInstance.post(
                ModulePermissionEndpoints.PageGuideline.GetWithFilterUnauth,
                data
            );

            if (response.data)
                return response.data;

            throw new Error("Object not found");
        } catch (error) {
            console.error('Error getting Page Guideline:', error);
            throw error;
        }
    }, []);


    return { CreatePageGuideline, UpdatePageGuideline, DeletePageGuideline, GetPageGuidelineByPageId };
}

// Module Permission
//--------------------------------

export const useGetLoggedInUserModulePermissions = (moduleId) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(moduleId ? ModulePermissionEndpoints.ModulePermission.GetLoggedInUserModulePermissions(moduleId) : null, { ...authenticatedSwrOptions, moduleId });

    return useMemo(() => ({
        modulePermissions: data || [],
        modulePermissionsLoading: isLoading,
        modulePermissionsError: error,
        modulePermissionsValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

// Change the filter later on
export const useGetModulePermissionsFiltered = (filter) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.ModulePermission.GetAllWithFilter, { ...authenticatedSwrOptions, filter });

    return useMemo(() => ({
        filteredModulePermissions: data || [],
        filteredModulePermissionsLoading: isLoading,
        filteredModulePermissionsError: error,
        filteredModulePermissionsValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetModulePermissionById = (modulePermissionId) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.ModulePermission.GetById(modulePermissionId), { ...authenticatedSwrOptions });

    return useMemo(() => ({
        modulePermission: data || {},
        modulePermissionLoading: isLoading,
        modulePermissionError: error,
        modulePermissionValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetModulePermissions = () => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.ModulePermission.GetAll, { ...authenticatedSwrOptions });

    return useMemo(() => ({
        modulePermissions: data || [],
        modulePermissionsLoading: isLoading,
        modulePermissionsError: error,
        modulePermissionsValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}


// Permission
//--------------------------------

export const useGetPermissionById = (permissionId) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Permission.GetById(permissionId), { ...authenticatedSwrOptions });

    return useMemo(() => ({
        permission: data || {},
        permissionLoading: isLoading,
        permissionError: error,
        permissionValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetPermissionsFiltered = (filter) => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Permission.GetAllWithFilter, { ...authenticatedSwrOptions, filter });

    return useMemo(() => ({
        filteredPermissions: data || [],
        filteredPermissionsLoading: isLoading,
        filteredPermissionsError: error,
        filteredPermissionsValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}

export const useGetPermissions = () => {
    const { data, error, isLoading, isValidating } = useAuthenticatedSWR(ModulePermissionEndpoints.Permission.GetAll, { ...authenticatedSwrOptions });

    return useMemo(() => ({
        permissions: data || [],
        permissionsLoading: isLoading,
        permissionsError: error,
        permissionsValidating: isValidating,
    }), [data, error, isLoading, isValidating]);
}
